import { Fragment, type ReactNode } from "react";

import type { CurrentView } from "../../types";
import NestedTagRow from "./NestedTagRow";
import { type TagSection, useTagFilterSections } from "./useTagFilterSections";

type RenderSection = (section: {
	title: string;
	children: ReactNode;
	index: number;
	isLast: boolean;
}) => ReactNode;

interface Props {
	sectionTags: TagSection[];
	selectedCheckboxMap?: Record<string, boolean>;
	handleSelectCheckbox: (distKey: string, locked?: boolean) => void;
	disableAll?: boolean;
	isDiagnostic?: boolean;
	hasAccess?: boolean;
	currentView?: CurrentView;
	maxFilterDepth: number;
	renderSection?: RenderSection;
}

const defaultRenderSection: RenderSection = ({ title, children }) => (
	<Fragment key={title}>{children}</Fragment>
);

const TagFiltersList = ({
	sectionTags,
	selectedCheckboxMap,
	handleSelectCheckbox,
	disableAll,
	isDiagnostic,
	hasAccess,
	currentView = "Filter",
	maxFilterDepth,
	renderSection = defaultRenderSection,
}: Props) => {
	const { visibleSections, expandedMap, setExpanded } = useTagFilterSections({
		sectionTags,
		selectedCheckboxMap,
		maxFilterDepth,
		currentView,
	});

	return (
		<>
			{visibleSections.map(({ title, rows }, index) => {
				const children = (
					<>
						{rows.map((row) => (
							<NestedTagRow
								key={row.tag.id}
								tag={row.tag}
								level={row.level}
								checked={!!selectedCheckboxMap?.[row.tag.id]}
								expanded={!!expandedMap[row.tag.id]}
								hasCheckedDescendant={row.hasCheckedDescendant}
								hasChildren={row.hasChildren}
								disableAll={disableAll}
								isDiagnostic={isDiagnostic}
								hasAccess={hasAccess}
								maxFilterDepth={maxFilterDepth}
								handleSelectCheckbox={handleSelectCheckbox}
								setExpanded={setExpanded}
							/>
						))}
					</>
				);
				return renderSection({
					title,
					children,
					index,
					isLast: index === visibleSections.length - 1,
				});
			})}
		</>
	);
};

export default TagFiltersList;
export { NestedTagRow, useTagFilterSections };
export type { TagSection };
